/**
 * Estampar el referido: el código que el navegador guardó cuando la persona
 * abrió el enlace, mandado al backend ahora que ya se sabe QUIÉN es.
 *
 * Es la segunda mitad de `referralCapture`. La captura pasa antes de que exista
 * la sesión (ver el comentario de ese archivo); esto pasa cuando ya hay sesión y
 * membresía, que es lo único a lo que el backend puede colgar la atribución.
 *
 * ── SILENCIOSA POR DISEÑO ──────────────────────────────────────────────────
 *
 * Esto es atribución, no una función del producto: nadie que entra a su panel
 * está esperando el resultado de esta llamada, y no hay ninguna frase honesta
 * que decirle si falla ("no pudimos acreditar a quien te invitó" es un problema
 * nuestro, no suyo). Así que no devuelve error, no pinta nada y no reintenta en
 * caliente.
 *
 * ── QUÉ SE BORRA Y QUÉ SE GUARDA ───────────────────────────────────────────
 *
 * 2xx: quedó estampado. 4xx: el backend dijo que ese código no sirve —inválido,
 * ya usado, o esta cuenta ya tiene referidor— y repetirlo mañana va a dar lo
 * mismo. En los dos casos el pendiente se borra.
 *
 * 5xx o red caída: no sabemos nada del código, sólo que el servidor no contestó.
 * Se deja donde está y la próxima sesión lo intenta de nuevo, hasta que venza a
 * los 30 días.
 */
import { apiFetch } from '~/lib/api';
import { clearPendingReferral, getPendingReferral } from '~/lib/referralCapture';

/**
 * Una sola llamada por vida de la página. El layout la dispara desde un efecto
 * que corre en cada navegación con el alta completa, y sin esto una tarde de
 * uso serían veinte POST idénticos.
 */
let settled = false;
let inflight: Promise<void> | null = null;

/** Sólo para las pruebas: el estado de módulo no se puede reusar sin esto. */
export function resetReferralSync(): void {
  settled = false;
  inflight = null;
}

export function syncPendingReferral(): Promise<void> {
  if (settled) return Promise.resolve();
  if (inflight) return inflight;

  const code = getPendingReferral();
  if (!code) {
    settled = true;
    return Promise.resolve();
  }

  inflight = (async () => {
    try {
      const res = await apiFetch('/job-apply/api/referral', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (res.ok || (res.status >= 400 && res.status < 500)) {
        clearPendingReferral();
        settled = true;
        return;
      }

      // 5xx: el código puede seguir siendo bueno. Se queda para la próxima.
      console.warn('[referral] el servidor no pudo estampar el referido', res.status);
    } catch {
      // Red caída. Mismo caso que el 5xx.
      console.warn('[referral] sin conexión para estampar el referido');
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}
